// pages/module-pages/support/tasks/TasksPage.tsx
import { useState, useMemo } from "react";
import { Plus, Search, Filter, X, AlertCircle, Play, Clock, Pause } from "lucide-react";
import { TasksList } from "../../../../components/features/support/tasks/TasksList";
import DeleteConfirmationModal from "../../../../components/ui/DeleteConfirmationModal";
import useTasks from "../../../../hooks/support/tasks/useTasks";
import { Task, TaskPriority, TaskStatus } from "../../../../types/features/support/Task";
import { alertSuccess, alertError } from "../../../../helpers/alertError";
import PageListLayout from "../../../../layouts/PageListLayout";
import TaskSaveModal from "../../../../components/features/support/tasks/TaskSaveModal";
import useAuth from "../../../../hooks/auth/useAuth";
import { TaskDetailModal } from "../../../../components/features/support/tasks/TaskDetailModal";
import { AddNoteModal } from "../../../../components/features/support/tasks/AddNoteModal"; // ✅
import { taskService } from "../../../../services/support/taskService"; // ✅
import useToken from "../../../../hooks/auth/useToken"; // ✅

export default function TasksPage() {
  const { tasks, loading, pagination, fetchTasks, deleteTask } = useTasks();
  const { user } = useAuth();
  const { token } = useToken(); // ✅

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPriority, setSelectedPriority] = useState<TaskPriority | "">("");
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus | "">("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [taskToViewId, setTaskToViewId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [taskNote, setTaskNote] = useState<Task | null>(null); // ✅

  // ============================================
  // 📊 Compteurs des tâches de l'utilisateur
  // ============================================
  const myTasks = useMemo(() => {
    if (!user?.id) return [];
    return tasks.filter((t) => t.agents?.some((a) => a.id === user.id));
  }, [tasks, user?.id]);

  const myCounts = useMemo(
    () => ({
      nouveau: myTasks.filter((t) => t.status === "nouveau").length,
      en_cours: myTasks.filter((t) => t.status === "en_cours").length,
      en_attente: myTasks.filter((t) => t.status === "en_attente").length,
    }),
    [myTasks]
  );

  const totalMine = myCounts.nouveau + myCounts.en_cours + myCounts.en_attente;

  // ============================================
  // 🎯 Filtrage
  // ============================================
  const filteredTasks = useMemo(() => {
    let result = tasks;

    if (onlyMine && user?.id) {
      result = result.filter((t) => t.agents?.some((a) => a.id === user.id));
    }
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      result = result.filter(
        (t) =>
          t.title.toLowerCase().includes(s) ||
          t.description?.toLowerCase().includes(s) ||
          t.client?.full_name?.toLowerCase().includes(s)
      );
    }
    if (selectedPriority) {
      result = result.filter((t) => t.priority === selectedPriority);
    }
    if (selectedStatus) {
      result = result.filter((t) => t.status === selectedStatus);
    }
    return result;
  }, [tasks, onlyMine, user?.id, searchTerm, selectedPriority, selectedStatus]);

  const stats = {
    total: tasks.length,
    nouveau: tasks.filter((t) => t.status === "nouveau").length,
    en_cours: tasks.filter((t) => t.status === "en_cours").length,
    en_attente: tasks.filter((t) => t.status === "en_attente").length,
    resolu: tasks.filter((t) => t.status === "resolu").length,
  };

  const clearFilters = () => {
    setSelectedPriority("");
    setSelectedStatus("");
    setSearchTerm("");
    setOnlyMine(false);
  };

  const hasActiveFilters = selectedPriority !== "" || selectedStatus !== "" || searchTerm.trim() !== "" || onlyMine;

  const handleEdit = (task: Task) => {
    setTaskToEdit(task);
    setModalOpen(true);
  };

  const handleDelete = (task: Task) => {
    setTaskToDelete(task);
  };

  // ✅ Sauvegarde de la note
  const handleSaveNote = async (note: string) => {
    if (!taskNote) return;
    await taskService.updateNote(taskNote.id, note || null, token);
    alertSuccess(note ? "Note enregistrée" : "Note supprimée");
    setTaskNote(null);
    fetchTasks(currentPage, pagination.perPage);
  };

  const confirmDelete = async () => {
    if (!taskToDelete) return;
    setIsDeleting(true);
    const success = await deleteTask(taskToDelete.id);
    setIsDeleting(false);
    if (success) {
      setTaskToDelete(null);
      alertSuccess("Tâche supprimée avec succès");
      fetchTasks(currentPage, pagination.perPage);
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchTasks(page, pagination.perPage, {
      status: selectedStatus || undefined,
      priority: selectedPriority || undefined,
    });
  };

  const handleBannerClick = (status?: TaskStatus) => {
    setOnlyMine(true);
    setSelectedStatus(status ?? "");
  };

  return (
    <PageListLayout title="Gestion des tâches">
      {/* BANNIÈRE D'ALERTE — Mes tâches */}
      {user?.id && totalMine > 0 && (
        <div className="bg-orange-50 border-b border-orange-200">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600 shrink-0" />
                <p className="text-sm font-medium text-orange-800">
                  Vous avez <strong>{totalMine}</strong> tâche{totalMine > 1 ? "s" : ""} à gérer
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {myCounts.nouveau > 0 && (
                  <button
                    onClick={() => handleBannerClick("nouveau")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium hover:bg-blue-200 transition-colors"
                  >
                    <Play className="w-3.5 h-3.5" />À démarrer ({myCounts.nouveau})
                  </button>
                )}
                {myCounts.en_cours > 0 && (
                  <button
                    onClick={() => handleBannerClick("en_cours")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200 transition-colors"
                  >
                    <Clock className="w-3.5 h-3.5" />
                    En cours ({myCounts.en_cours})
                  </button>
                )}
                {myCounts.en_attente > 0 && (
                  <button
                    onClick={() => handleBannerClick("en_attente")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-medium hover:bg-orange-200 transition-colors"
                  >
                    <Pause className="w-3.5 h-3.5" />
                    En attente ({myCounts.en_attente})
                  </button>
                )}
                {onlyMine && (
                  <button
                    onClick={() => {
                      setOnlyMine(false);
                      setSelectedStatus("");
                    }}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs text-orange-700 hover:text-orange-900 transition-colors"
                    title="Voir toutes les tâches"
                  >
                    <X className="w-3.5 h-3.5" />
                    Voir tout
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Les tâches</h1>
              <p className="text-sm text-gray-500 mt-1">Consignes internes entre administrateurs</p>
            </div>
            <button
              onClick={() => {
                setTaskToEdit(null);
                setModalOpen(true);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:opacity-90"
            >
              <Plus size={18} />
              Nouvelle consigne
            </button>
          </div>

          {/* Stats globales */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-700/70">Total</p>
              <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Nouveau</p>
              <p className="text-2xl font-bold text-blue-700">{stats.nouveau}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
              <p className="text-xs text-purple-700/70">En cours</p>
              <p className="text-2xl font-bold text-purple-700">{stats.en_cours}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
              <p className="text-xs text-orange-700/70">En attente</p>
              <p className="text-2xl font-bold text-orange-700">{stats.en_attente}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Résolu</p>
              <p className="text-2xl font-bold text-green-700">{stats.resolu}</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une tâche..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* Filtres */}
          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-primary hover:opacity-80 flex items-center gap-1"
                    >
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                    <select
                      value={selectedPriority}
                      onChange={(e) => setSelectedPriority(e.target.value as TaskPriority | "")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">Toutes</option>
                      <option value="basse">Basse</option>
                      <option value="moyenne">Moyenne</option>
                      <option value="haute">Haute</option>
                      <option value="critique">Critique</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value as TaskStatus | "")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">Tous</option>
                      <option value="nouveau">Nouveau</option>
                      <option value="en_cours">En cours</option>
                      <option value="en_attente">En attente</option>
                      <option value="resolu">Résolu</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assignation</label>
                    <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={onlyMine}
                        onChange={(e) => setOnlyMine(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Mes tâches uniquement</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        <TasksList
          tasks={filteredTasks}
          loading={loading}
          currentUserId={user?.id}
          onView={(t) => setTaskToViewId(t.id)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddNote={(t) => setTaskNote(t)} // ✅
        />

        {pagination.total > 0 && (
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Précédent
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} sur {pagination.lastPage} ({pagination.total} tâches)
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.lastPage}
              className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Modal création/édition */}
      <TaskSaveModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setTaskToEdit(null);
        }}
        onSuccess={() => {
          setModalOpen(false);
          alertSuccess(taskToEdit ? "Tâche modifiée" : "Tâche créée");
          setTaskToEdit(null);
          fetchTasks(currentPage, pagination.perPage);
        }}
        initialData={taskToEdit}
      />

      {/* ✅ Modal note */}
      <AddNoteModal
        isOpen={Boolean(taskNote)}
        onClose={() => setTaskNote(null)}
        initialNote={taskNote?.note ?? ""}
        taskTitle={taskNote?.title}
        onSave={handleSaveNote}
      />

      {/* Suppression */}
      <DeleteConfirmationModal
        isOpen={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={confirmDelete}
        title="Supprimer la tâche"
        message={`Supprimer la tâche "${taskToDelete?.title}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      {/* Détail */}
      <TaskDetailModal
        isOpen={!!taskToViewId}
        onClose={() => setTaskToViewId(null)}
        taskId={taskToViewId}
        currentUserId={user?.id}
        onEdit={(t) => {
          setTaskToViewId(null);
          setTaskToEdit(t);
          setModalOpen(true);
        }}
        onDelete={(t) => {
          setTaskToViewId(null);
          setTaskToDelete(t);
        }}
        onTaskUpdated={() => fetchTasks(currentPage, pagination.perPage)}
      />
    </PageListLayout>
  );
}
